// /en/compare — English mirror of /compare (Phase P29-EN)
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { collectionPageSchema } from "@/lib/aeo";
import { TrustLayer } from "@/components/TrustLayer";
import { ArrowRight, Sparkles } from "lucide-react";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Compare NYC Legal & Notary vs Alternatives — Translation · Notary · Visa | 2026";
const DESC =
  "Compare NYC Legal & Notary against freelancers, DIY, embassies, lawyers, and online notaries across price. Send your documents for a free eligibility check.";

export const Route = createFileRoute("/en/compare/")({
    loader: async () => {
    const { COMPARISONS } = await import("@/data/comparisons");
    return { __d: { COMPARISONS } };
  },
head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/compare", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/compare", "en")},
      { name: "keywords", content: "best translation agency Thailand, notary Bangkok comparison, NYC Legal review" },
      { property: "og:title", content: ctaTitle(TITLE, "/en/compare", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/compare", "en")},
      { property: "og:url", content: `${SITE}/en/compare` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/compare` },
      ...hreflangLinks("/compare", "/en/compare"),
    ],
    scripts: [
      collectionPageSchema({ path: "/en/compare", name: TITLE, description: DESC }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "Compare", item: `${SITE}/en/compare` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "NYC vs alternatives — comparison index",
          numberOfItems: loaderData!.__d.COMPARISONS.length,
          itemListElement: loaderData!.__d.COMPARISONS.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE}/en/compare/${c.slug}`,
            name: c.titleEn,
          })),
        }),
      },
    ],
  }),
  component: EnCompareHub,
});

function EnCompareHub() {
  const { COMPARISONS } = (Route.useLoaderData() as { __d: typeof import("@/data/comparisons") }).__d;
  return (
    <main className="min-h-screen bg-white">
      <section className="border-b border-amber-500/20 bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <nav className="mb-4 text-xs text-white/70" aria-label="Breadcrumb">
            <Link to="/en" className="hover:text-amber-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-amber-300">Compare</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <Sparkles className="h-3.5 w-3.5" /> Comparison 2026
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            Translation · Notary · Visa<br />
            <span className="text-amber-400">Which provider fits you best?</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            Side-by-side comparisons of <strong className="text-white">NYC Legal & Notary</strong> versus DIY,
            freelancers, lawyers, embassies, VFS Global, online notaries, and other alternatives — across {COMPARISONS.length} scenarios.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">All comparisons ({COMPARISONS.length})</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {COMPARISONS.map((c) => (
            <Link
              key={c.slug}
              to="/en/compare/$topic"
              params={{ topic: c.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">vs {c.altLabelEn}</div>
              <h3 className="mt-1 font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">
                {c.titleEn}
              </h3>
              <p className="mt-2 line-clamp-3 text-[13px] text-slate-600">{c.descEn}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700">
                Read comparison <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        <RelatedHubGrid locale="en" />
      </div>

        <p className="mt-10 text-sm text-slate-600">
          ภาษาไทย? <Link to="/compare" className="font-semibold text-amber-700 underline">/compare</Link>
        </p>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
